import _ from 'lodash';
import normalizeAccessories from './normalizeAccessories';
import normalizePackagesWithStructure from './normalizePackages';

const every = _.every;
var get = _.get;

function normalizeOptions(fullConfig) {
  const { config, modelMatrix } = fullConfig;
  const accessories = _.flatMap(config.ACCESSORIES);
  const axelRatios = _.flatMap(modelMatrix.axleRatio);
  const engines = _.flatMap(modelMatrix.engine);
  const transmissions = _.flatMap(modelMatrix.transmission);
  const exteriorColors = _.flatMap(config.OPTIONS.COLOR.exterior, 'items');
  const interiorColors = _.flatMap(config.OPTIONS.COLOR.interior, 'items');
  const stripeColors = _.flatMap(config.OPTIONS.COLOR['exterior.stripecolor']);
  const topColors = _.flatMap(config.OPTIONS.COLOR['exterior.topcolor']);
  const packages = _.flatMap(config.OPTIONS.PACKAGES);
  const exterior = _.flatMap(config.OPTIONS.EXTERIOR);
  const interior = _.flatMap(config.OPTIONS.INTERIOR);
  return [].concat(
    accessories,
    axelRatios,
    engines,
    transmissions,
    exteriorColors,
    interiorColors,
    stripeColors,
    topColors,
    packages,
    exterior,
    interior
  );
}

function normalizePackages(pkgs) {
  //
  // This done is to make the lite data shaped like the normal packages data
  // description, disclaimer, id, large image url, small image url, video url
  let packages = _.flatMap(pkgs);
  if (packages.length && packages[0].descriptions) {
    packages.forEach(pkg => {
      pkg.id = pkg.code;
      let descriptions = _.flatMap(pkg.descriptions);
      descriptions.forEach((description, id) => {
        pkg.packageOptions.push({
          description: description,
          id: id,
          disclaimer: '',
          largeImageUrl: '',
          smallImageUrl: '',
          videoUrl: ''
        });
      });
    });
  }

  _.each(packages, function(pkg) {
    pkg.numericMsrp = get(pkg, 'msrpWidget.actual.value') || 0;
  });

  return _.mapKeys(pkgs, (value, key) => _.last(_.split(key, '_')));
}

function existingNormalizeFullConfig(data) {
  var selectedStyle = _.find(data.modelMatrix.styleInformation, 'selected');

  data.modelMatrix.selectedStyleId = selectedStyle.id;

  _.each(data.modelMatrix.bodyTypes, function(config) {
    if (config.cabSize) {
      config.icon = 'cab_size_' + config.cabSize;
      config.iconClass = 'cab-size';
    } else if (config.wheelBaseType) {
      config.icon = 'wheel_base_' + config.wheelBaseType;
      config.iconClass = 'wheel-base';
    }
    if (config.icon) {
      config.icon = config.icon.replace(/_/g, '-').toLowerCase();
    }
  });

  _.each(data.modelMatrix.driveTypes, function(driveType) {
    const normalizedIds = {
      '2RM': '2WD',
      '4RM': '4WD',
      TA: 'FWD',
      TI: 'AWD',
      PA: 'RWD'
    };

    const normalizedId = normalizedIds[driveType.id] || driveType.id;
    driveType.icon = 'Single_' + normalizedId + '.png';
    driveType.iconClass = 'drive-type-' + normalizedId;
  });

  data.config.OPTIONS.PACKAGES = normalizePackages(
    data.config.OPTIONS.PACKAGES
  );

  var optionsArray = normalizeOptions(data);

  //TODO: descriptions need updated in the data. remove this ASAP.
  _.each(optionsArray, function(option) {
    var badStrings = new RegExp('([\\w+]{2,}\\/)([\\w]{2,})', 'gim'),
      match; //eslint-disable-line no-unused-vars
    while ((match = badStrings.exec(option.description))) {
      //eslint-disable-line no-cond-assign
      option.description =
        option.description &&
        option.description.replace(badStrings, '$1&shy;$2');
    }
  });

  // conflict check
  var add = data.config.TOGGLE_CHECK.addOptions;
  var del = data.config.TOGGLE_CHECK.deleteOptions;
  var choice = data.config.TOGGLE_CHECK.userChoiceOptions;

  data.hasConflict =
    (add && add.length > 0) ||
    (del && del.length > 0) ||
    (choice && choice.length > 0);

  //lots of services break when we don't remove certain RPO codes from this list.
  _.remove(data.config.VEHICLE.SELECTED_RPO_LIST, function(rpo) {
    return _.startsWith(rpo, '-');
  });

  data.options = optionsArray.reduce(function(hash, option) {
    hash[option.id] = option;
    return hash;
  }, {});

  return data;
}

function findSelected(options) {
  return _.find(options, 'selected');
}

function findSelectedOrFirst(options) {
  return findSelected(options) || _.first(options);
}

function normalizeMsrp(msrp) {
  return {
    isActualPrice: msrp.priceState === 'Actual',
    isConfidencePrice: msrp.confidencePrice,
    acTax: get(msrp, 'acTax.value'),
    dealerFee: get(msrp, 'dealerFee.value'),
    baseMsrp: get(msrp, 'baseMsrp.value'),
    accessoriesInvoiceTotal: get(msrp, 'accInvoiceTotal.value'),
    accessoriesTotal: Math.floor(get(msrp, 'accTotal.value')),
    destination: get(msrp, 'destination.value'),
    gasGuzzlerTax: get(msrp, 'gasGuzzlerTax.value'),
    opdTotal: get(msrp, 'opdTotal.value'),
    packageIncentives: get(msrp, 'packageIncentives.value'),
    optionsTotal: get(msrp, 'optionsTotal.value'),
    totalMsrpWithOptions: get(msrp, 'vehicleOptionsTotal.value'),
    totalMsrpBeforeOpd: get(msrp, 'vehicleTotalBeforeOpd.value'),
    totalMsrp: get(msrp, 'vehicleTotal.value'),
    msrpForResidualCalculation:
      get(msrp, 'msrpForResidualCalculation.value') || 0,
    vehicleTotalEmployeePriceForPe:
      get(msrp, 'vehicleTotalEmployeePriceForPe.value') || 0,
    supplierPrice: get(msrp, 'supplierPrice.value'),
    evIncentiveBeforeTax:
      get(msrp, 'marketIncentives.evIncentive.beforeTax') || 0,
    evIncentiveAfterTax:
      get(msrp, 'marketIncentives.evIncentive.afterTax') || 0,
    employeePrice: get(msrp, 'vehicleTotalInvoice.value') || 0,
    miscFees: get(msrp, 'miscFees'),
    flt: get(msrp, 'flt')
  };
}

const normalizedEligiblePrice = (eligiblePrice, assetsBrand) => {
  if (typeof eligiblePrice === 'undefined' || assetsBrand !== 'ff') {
    return true;
  }
  return eligiblePrice;
};

const computeModelEligibility = styles =>
  every(styles, { eligibleForInvoicePrice: false });

function normalizeFullConfig(fullConfig, modelInfo) {
  let { modelMatrix, config } = fullConfig;
  const { assetsBrand } = modelInfo;

  let styles = modelMatrix.styleInformation.map(style => {
    return _.assign({}, style, {
      mmc: style.vehicleInfo.code.replace(/_.*/, ''),
      peg: _.head(style.vehicleInfo.codeForCGI.split('-')),
      defaultExteriorColor: style.vehicleInfo.defaultColor,
      eligibleForInvoicePrice: normalizedEligiblePrice(
        style.eligibleForInvoicePrice,
        assetsBrand
      )
    });
  });

  var style = findSelected(styles);
  var driveType =
    findSelected(modelMatrix.driveTypes) ||
    findSelected(modelMatrix.motorizations);
  var bodyType = findSelectedOrFirst(modelMatrix.bodyTypes);
  var engine = findSelected(modelMatrix.engine);
  var axleRatio = findSelected(modelMatrix.axleRatio);
  var transmission = findSelected(modelMatrix.transmission);
  var motorization = findSelected(modelMatrix.motorizations);

  var colors = config.OPTIONS.COLOR;

  var interiorColor = findSelectedOrFirst(_.flatMap(colors.interior, 'items'));
  var exteriorColor = findSelectedOrFirst(_.flatMap(colors.exterior, 'items'));

  var styleId = _.get(style, 'id');
  var selections = {
    styleId: styleId,
    trimName: _.get(style, 'trimName', ''),
    driveType: _.get(driveType, 'id'),
    rearWheelType: _.get(bodyType, 'rearWheelType', ''),
    motorization: _.get(motorization, 'id'),
    bodyTypeId: _.get(bodyType, 'id'),
    bodyType: _.get(bodyType, 'id', ''),
    engine: _.get(engine, 'id'),
    cabSize: _.get(bodyType, 'cabSize', ''),
    axleRatio: _.get(axleRatio, 'id'),
    transmission: _.get(transmission, 'id'),
    rpos: _.get(config, 'VEHICLE.SELECTED_RPO_LIST'),
    accessories: _.get(config, 'VEHICLE.SELECTED_ACCESSORY_LIST'),
    interiorColor: _.get(interiorColor, 'id'),
    exteriorColor: _.get(exteriorColor, 'id')
  };

  var msrp = normalizeMsrp(config.price);

  var normalizedPackages = normalizePackagesWithStructure(config);

  var incentives = {
    deadline: style.vehicleInfo.nationalIncentiveEndDate,
    value: style.vehicleInfo.nationalIncentiveOfferValue
  };

  const exterior = config.OPTIONS.EXTERIOR;
  const transmissions = modelMatrix.transmission || [];

  var flattenedProps = {
    msrp: msrp,
    incentives: incentives,
    styles,
    style: style,
    styleId: styleId,
    eligibleForInvoicePrice: normalizedEligiblePrice(
      config.eligibleForInvoicePrice,
      assetsBrand
    ),
    isModelIneligible: computeModelEligibility(modelMatrix.styleInformation),
    engines: modelMatrix.engine,
    engine: engine,
    bodyTypes: modelMatrix.bodyTypes,
    bodyType: bodyType,
    driveTypes: modelMatrix.driveTypes,
    driveType: driveType,
    motorizations: modelMatrix.motorizations || [],
    motorization: motorization,
    transmissions,
    transmission: transmission,
    axleRatios: modelMatrix.axleRatio,
    axleRatio: axleRatio,
    options: fullConfig.options,
    colors: {
      exterior: colors.exterior || [],
      interior: colors.interior || [],
      stripe: colors['exterior.stripecolor'] || [],
      top: colors['exterior.topcolor'] || []
    },
    packages: normalizedPackages,
    accessories: normalizeAccessories(config),
    interior: config.OPTIONS.INTERIOR,
    exterior,
    technicalSpecifications: config.technicalSpecifications,
    ss: config.ss,
    selections: selections,
    mmc: style.mmc,
    peg: style.peg
  };

  return _.assign(fullConfig, flattenedProps);
}

export default modelInfo => data =>
  normalizeFullConfig(existingNormalizeFullConfig(data), modelInfo);
